import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, Typography, Row, Col, Empty } from 'antd';
import { message as antdMessage } from 'antd';
import ChatWindow from '../components/Chat/ChatWindow';
import ConversationList from '../components/Chat/ConversationList';
import { Customer360Panel } from '../components/Chat/Customer360Panel';
import { chatService } from '../services/chatService';
import api from '../services/api';

const { Title } = Typography;

interface Conversation {
  sender_id: number;
  last_message_date: string;
  unread_count: number;
  ticket?: {
    id: number;
    status: 'unassigned' | 'in_progress' | 'closed';
    assignedAgentId: number | null;
    assignedAgentName: string | null;
    category?: string;
  };
  sender: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    customerProfile?: {
      first_name: string;
      last_name: string;
    };
  };
}

const ChatPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [show360Panel, setShow360Panel] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showClosed, setShowClosed] = useState<boolean>(false);
  const selectedUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  const token = localStorage.getItem('token') || '';
  const userStr = localStorage.getItem('currentUser');
  const user = userStr ? JSON.parse(userStr) : null;
  // Chat réservé à Agent, Team Lead (manager) et Super Admin — admin KO (acces_dashboard.md)
  const isAuthorizedStaff = user && ['super_admin', 'manager', 'agent'].includes(user.role);

  const loadConversations = async (closedMode?: boolean) => {
    try {
      setIsLoading(true);
      const mode = closedMode !== undefined ? closedMode : showClosed;
      const response = await api.get(`/chat/conversations${mode ? '?includesClosed=true' : ''}`);
      if (response.data.success) {
        const list = response.data.data || [];
        setConversations(list);
        // Présélectionner automatiquement la première conversation si aucune n'est sélectionnée
        if (!selectedUserIdRef.current && list.length > 0 && !mode) {
          setSelectedUserId(Number(list[0].sender_id));
        }
      }
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    const isClosed = tab === 'closed';
    setShowClosed(isClosed);
    setSelectedUserId(null);
    loadConversations(isClosed);
  };

  useEffect(() => {
    if (token && isAuthorizedStaff) {
      // S'assurer que le socket chat est bien connecté
      if (!chatService.getConnectionStatus()) {
        chatService.connect(token, user?.id?.toString() || '');
      }

      loadConversations();
      
      const handleMessageForCounter = (msg: any) => {
        if (msg.sender_role !== 'customer') return;
        const currentSelectedUserId = selectedUserIdRef.current;
        
        if (Number(currentSelectedUserId) !== Number(msg.sender_id)) {
          const senderName = msg.sender?.customerProfile?.first_name
            ? `${msg.sender.customerProfile.first_name} ${msg.sender.customerProfile.last_name || ''}`
            : msg.sender?.first_name
            ? `${msg.sender.first_name} ${msg.sender.last_name || ''}`
            : msg.sender?.email || 'Un client';
          
          antdMessage.info({
            content: `💬 ${senderName.trim()}: ${(msg.message || msg.content || '').substring(0, 50)}${(msg.message || msg.content || '').length > 50 ? '...' : ''}`,
            duration: 5,
            onClick: () => setSelectedUserId(Number(msg.sender_id)),
            style: { cursor: 'pointer' }
          });
        }
        
        loadConversations();
      };
      
      chatService.onMessage(handleMessageForCounter);
      
      return () => {
        chatService.offMessage(handleMessageForCounter);
      };
    }
  }, [token, isAuthorizedStaff]);
  
  useEffect(() => {
    if (selectedUserId) {
      setConversations((prev) => {
        return prev.map((conv) => {
          if (Number(conv.sender_id) === Number(selectedUserId)) {
            return { ...conv, unread_count: 0 };
          }
          return conv;
        });
      });
    }
  }, [selectedUserId]);

  const handleAssignConversation = async (targetUserId: number) => {
    try {
      await chatService.assignConversation(targetUserId);
      antdMessage.success('Conversation prise en charge avec succès');
      loadConversations();
    } catch (error: any) {
      antdMessage.error(error.response?.data?.error || 'Erreur lors de la prise en charge');
    }
  };

  const handleCloseTicket = async (category: string, notes: string) => {
    if (!selectedUserId) return;
    try {
      await chatService.closeConversation(selectedUserId, category, notes);
      antdMessage.success('Ticket support clôturé avec succès');
      loadConversations();
    } catch (error: any) {
      antdMessage.error(error.response?.data?.error || 'Erreur lors de la clôture du ticket');
    }
  };

  const handleDeleteConversation = async (userIdToDelete: number) => {
    try {
      await api.delete(`/chat/conversation/${userIdToDelete}`);
      antdMessage.success('Conversation supprimée avec succès');
      if (selectedUserId === userIdToDelete) {
        setSelectedUserId(null);
      }
      loadConversations();
    } catch (error: any) {
      console.error('Erreur suppression conversation:', error);
      antdMessage.error(error.response?.data?.error || 'Impossible de supprimer la conversation');
    }
  };

  // Seuls les membres staff support ont accès au chat
  if (user && !isAuthorizedStaff) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!token || !user) {
    return (
      <div style={{ maxWidth: 1200, margin: '32px auto' }}>
        <Card>
          <div>Veuillez vous connecter pour accéder au chat</div>
        </Card>
      </div>
    );
  }

  const selectedConv = conversations.find(c => Number(c.sender_id) === Number(selectedUserId));
  const activeTicket = selectedConv?.ticket;

  return (
    <div
      style={{
        height: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        overflow: 'hidden',
      }}
    >
      <Row gutter={[16, 16]} style={{ height: '100%', overflow: 'hidden' }}>
        {/* 1. Liste des conversations (Gauche) */}
        <Col xs={24} md={8} lg={6} style={{ height: '100%' }}>
          <Card 
            style={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 16,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}
            bodyStyle={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}
          >
            <div 
              style={{ 
                padding: '12px 16px', 
                borderBottom: '1px solid #f0f0f0',
                flexShrink: 0,
                backgroundColor: '#0a543d',
                color: '#ffffff'
              }}
            >
              <Title level={5} style={{ margin: 0, fontWeight: 'bold', color: '#ffffff' }}>
                Center Call & Support Chat
              </Title>
            </div>
            
            <div style={{ flexGrow: 1, overflow: 'hidden' }}>
              <ConversationList
                conversations={conversations}
                selectedUserId={selectedUserId}
                currentUserId={user?.id}
                onSelectConversation={setSelectedUserId}
                onDeleteConversation={handleDeleteConversation}
                onAssignConversation={handleAssignConversation}
                onTabChange={handleTabChange}
                isLoading={isLoading}
              />
            </div>
          </Card>
        </Col>

        {/* 2. Fenêtre de Chat Support (Milieu) */}
        <Col
          xs={24}
          md={selectedUserId && show360Panel ? 10 : 16}
          lg={selectedUserId && show360Panel ? 11 : 18}
          style={{ height: '100%' }}
        >
          {selectedUserId ? (
            <ChatWindow
              token={token}
              userId={user.id.toString()}
              userName={user.name || user.username || 'Admin'}
              recipientId={selectedUserId}
              ticketStatus={activeTicket?.status || 'unassigned'}
              assignedAgentName={activeTicket?.assignedAgentName}
              onMessageSent={loadConversations}
              onAssign={() => handleAssignConversation(selectedUserId)}
              onCloseTicket={handleCloseTicket}
              showCustomer360Button={true}
              onToggleCustomer360={() => setShow360Panel(prev => !prev)}
            />
          ) : (
            <Card 
              style={{ 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: 16
              }}
            >
              <Empty description="Sélectionnez une conversation du centre d'appel pour commencer" />
            </Card>
          )}
        </Col>

        {/* 3. Volet Fiche Client 360° (Droite) */}
        {selectedUserId && show360Panel && (
          <Col xs={24} md={6} lg={7} style={{ height: '100%' }}>
            <Customer360Panel
              userId={selectedUserId}
              onClose={() => setShow360Panel(false)}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default ChatPage;
